import { Icon } from "@iconify/react";
import type { ReactNode } from "react";
import { toast } from "sonner";
import type { TicketLinkedEntityDto } from "@/api/tickets";
import { ExtendedInfoList, parseExtendedInfo } from "@/components/extended-info-list";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/locales/i18n-provider";

export type LinkedEntityRendererProps = { ticketId: string; link: TicketLinkedEntityDto };

export function LinkedEntityCard({
  link,
  title,
  type,
  icon,
  children,
}: {
  link: TicketLinkedEntityDto;
  title: string;
  type: string;
  icon: string;
  children?: ReactNode;
}) {
  const { t } = useI18n();
  return (
    <li className="rounded-xl border bg-card p-4">
      <div className="flex items-start gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <Icon className="size-5" icon={icon} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="wrap-break-word text-sm font-semibold text-foreground">{title}</h3>
            <Badge variant="secondary">{type}</Badge>
          </div>
          <div className="mt-1 flex min-w-0 items-center gap-1">
            <code className="min-w-0 flex-1 truncate text-xs text-muted-foreground" title={link.externalId}>
              {link.externalId}
            </code>
            <Button
              aria-label={t("tickets.linked.copyIdAria", { entityType: type })}
              size="icon-xs"
              type="button"
              variant="ghost"
              onClick={async () => {
                await navigator.clipboard.writeText(link.externalId);
                toast.success(t("tickets.linked.copiedToast"));
              }}
            >
              <Icon icon="tabler:copy" />
            </Button>
          </div>
          {link.resolutionStatus === "not_found" ? <p className="mt-1 text-xs text-muted-foreground">{t("tickets.linked.sourceRecordMissing")}</p> : null}
          {link.resolutionStatus === "unavailable" ? <p className="mt-1 text-xs text-muted-foreground">{t("tickets.linked.sourceUnavailable")}</p> : null}
        </div>
      </div>
      {children}
      {link.extendedInfoJson ? (
        <details className="mt-3 border-t pt-3">
          <summary className="cursor-pointer text-xs font-medium text-muted-foreground hover:text-foreground">{t("tickets.linked.viewExtendedData")}</summary>
          <ExtendedInfoList className="mt-2" entries={parseExtendedInfo(link.extendedInfoJson, t("extendedInfo.value"), t("extendedInfo.raw"))} />
        </details>
      ) : null}
    </li>
  );
}
